/**
 * 根据交易类型获取对应图标
 * @param txnType 交易类型
 * @returns 图标路径
 */
export const getTransactionIcon = (txnType?: string): string => {
  if (txnType === 'deposit') {
    return '/images/card-v2/card-recharge-icon.svg'
  }
  if (txnType === 'withdrawal') {
    return '/images/card-v2/card-transfer-icon.svg'
  }
  return '/images/card-v2/records-consumption-icon.svg'
}

/**
 * 卡形态枚举
 */
export enum CardFormFactor {
  Virtual = 'virtual_card',
  Physical = 'physical_card'
}

/**
 * 特性项接口（包含图标和文本）
 */
export interface FeatureItem {
  icon: string
  text: string
}

/**
 * 多语言 meta 数据接口
 */
interface LocalizedMeta {
  name: string
  description_list: Array<{
    icon: string
    text: string
  }>
}

/**
 * 解析卡产品 meta 返回值接口
 */
export interface ParsedCardMeta {
  name: string
  features: FeatureItem[]
}

/**
 * 解析卡产品 meta JSON
 * @param metaJson meta JSON 字符串
 * @param locale 当前语言（如 'zh-CN', 'en', 'ja' 等）
 * @returns 解析后的产品信息
 */
export const parseCardMeta = (
  metaJson?: string,
  locale: string = 'zh-CN'
): ParsedCardMeta => {
  if (!metaJson) {
    return { name: '', features: [] }
  }

  try {
    const parsed = JSON.parse(metaJson) as Record<string, LocalizedMeta>
    
    // 优先使用当前语言，如果不存在则使用 zh-CN 作为 fallback
    const localizedData = parsed[locale] || parsed['zh-CN'] || parsed['en']
    
    if (!localizedData) {
      return { name: '', features: [] }
    }

    const features: FeatureItem[] = (localizedData.description_list || []).map(item => ({
      icon: item.icon,
      text: item.text
    }))

    return {
      name: localizedData.name || '',
      features
    }
  } catch (error) {
    console.error('解析卡产品 meta 失败:', error)
    return { name: '', features: [] }
  }
}

/**
 * 判断是否为虚拟卡
 * @param formFactor 卡形态
 * @returns 是否为虚拟卡
 */
export const isVirtualCard = (formFactor?: string): boolean => {
  return formFactor === CardFormFactor.Virtual
}

/**
 * 判断是否为实体卡
 * @param formFactor 卡形态
 * @returns 是否为实体卡
 */
export const isPhysicalCard = (formFactor?: string): boolean => {
  return formFactor === CardFormFactor.Physical
}

/**
 * 根据卡形态获取卡类型标签
 * @param formFactor 卡形态
 * @returns 卡类型标签键（需要配合 t() 使用）
 */
export const getCardTypeLabel = (formFactor?: string): string => {
  if (formFactor === CardFormFactor.Physical) {
    return '实体卡'
  }
  return '电子卡'
}

/**
 * 第三方支付支持接口
 */
export interface PaymentSupport {
  name: string
  icon: string
  supported: boolean
}

/**
 * 解析 features JSON 获取支付支持
 * @param featuresJson features JSON 字符串
 * @returns 支付支持列表
 */
export const parsePaymentSupports = (featuresJson?: string): PaymentSupport[] => {
  if (!featuresJson) return []

  try {
    const features = JSON.parse(featuresJson) as {
      google_pay?: boolean
      apple_pay?: boolean
      alipay?: boolean
      wechat_pay?: boolean
    }

    const supports: PaymentSupport[] = []
    
    if (features.google_pay) {
      supports.push({ name: 'Google Pay', icon: '/images/card-v2/GooglePay.svg', supported: true })
    }
    if (features.apple_pay) {
      supports.push({ name: 'Apple Pay', icon: '/images/card-v2/ApplePay.svg', supported: true })
    }
    if (features.alipay) {
      supports.push({ name: 'Alipay', icon: '/images/card-v2/Alipay.svg', supported: true })
    }
    if (features.wechat_pay) {
      supports.push({ name: 'Wechat Pay', icon: '/images/card-v2/WeChat.svg', supported: true })
    }

    return supports
  } catch (error) {
    console.error('解析 features 失败:', error)
    return []
  }
}

